/**
 * Utility functions for DetailViewItem2 component
 *
 * Pure functions for data validation, adaptation, and URL/dimension checking.
 * These functions have no React dependencies and can be tested in isolation.
 *
 * @module DetailViewItem2.utils
 */

import type { DetailViewItem2Props } from './DetailViewItem2.types';
import type { Photo, ImageVariant } from '@/types/photo';
import type { ZoomLevel } from '@/hooks/useZoomPan';

/**
 * Get best URL from variant (publicUrl preferred, storageUrl fallback) - Feature 077
 */
function getBestVariantUrl(variant: ImageVariant | undefined): string | undefined {
  if (!variant) return undefined;
  return variant.publicUrl || variant.storageUrl;
}

/**
 * Get the actual URL for a specific variant without fallback logic
 * Used for URL comparison to detect which variant is loaded
 *
 * @param photo - Photo object with variant URLs
 * @param variantType - Specific variant type to get
 * @returns The actual URL for that variant, or undefined if it doesn't exist
 */
function getActualVariantUrl(
  photo: Photo,
  variantType: 'thumbnail' | 'medium' | 'full'
): string | undefined {
  // Handle stringified variants gracefully
  const variants = getVariantsArray(photo.variants as any);
  if (!variants) return undefined;

  return getBestVariantUrl(variants.find(v => v.variantType === variantType));
}

/**
 * Get variants array, handling stringified variants gracefully
 * Returns undefined if variants is stringified (data pipeline issue)
 */
export function getVariantsArray(
  variants: ImageVariant[] | string | undefined
): ImageVariant[] | undefined {
  if (typeof variants === 'string') {
    // Don't try to parse - this is a data pipeline error that should be fixed upstream
    return undefined;
  }
  return variants;
}

/**
 * Validate URL configuration against actual loaded variant
 * Zoom-aware: Considers current zoom level and container size when determining if mismatch is a problem
 */
export function validateUrlConfiguration(
  imageSource: string,
  photo: DetailViewItem2Props['photo'],
  actualVariantType: 'thumbnail' | 'medium' | 'full' | 'unknown',
  zoomLevel?: ZoomLevel,
  containerWidth?: number,
  containerHeight?: number
): { urlMismatch: boolean; urlClaimsVariant?: string; dataIssue?: string } {
  // Check for stringified variants array (data pipeline issue)
  if (typeof photo.photoData.variants === 'string') {
    return {
      urlMismatch: true,
      dataIssue: 'variants array is stringified',
    };
  }

  // Get properly typed variants array
  const variants = getVariantsArray(photo.photoData.variants as any);

  // Determine expected variant based on zoom level and container size
  let expectedVariant: 'thumbnail' | 'medium' | 'full' = 'medium';

  // High zoom levels always expect full resolution
  if (zoomLevel === 100 || zoomLevel === 200) {
    expectedVariant = 'full';
  }
  // At fit zoom, check if container is larger than medium variant
  else if (zoomLevel === 'fit' || zoomLevel === 50) {
    if (containerWidth && containerHeight && variants) {
      const medium = variants.find(v => v.variantType === 'medium');
      // If container is larger than medium variant, expect full resolution
      if (medium && (containerWidth > medium.width || containerHeight > medium.height)) {
        expectedVariant = 'full';
      }
    }
  }

  // Check which URL field the imageSource matches (without fallback)
  const matchesThumbnail = imageSource === getActualVariantUrl(photo.photoData, 'thumbnail');
  const matchesMedium = imageSource === getActualVariantUrl(photo.photoData, 'medium');
  const matchesFull = imageSource === getActualVariantUrl(photo.photoData, 'full');

  // If we're loading the expected variant for this zoom level, no problem
  if (actualVariantType === expectedVariant) {
    return { urlMismatch: false };
  }

  // Otherwise check for URL field mismatches
  if (matchesThumbnail && actualVariantType !== 'thumbnail') {
    return { urlMismatch: true, urlClaimsVariant: 'thumbnail' };
  }
  if (matchesMedium && !matchesFull && actualVariantType !== 'medium') {
    // Only flag if mediumUrl is unique (not same as fullUrl)
    return { urlMismatch: true, urlClaimsVariant: 'medium' };
  }
  if (matchesFull && !matchesMedium && actualVariantType !== 'full') {
    // Only flag if fullUrl is unique (not same as mediumUrl)
    return { urlMismatch: true, urlClaimsVariant: 'full' };
  }

  return { urlMismatch: false };
}

/**
 * Validate loaded dimensions against expected variant dimensions
 */
export function validateVariantDimensions(
  loadedWidth: number,
  loadedHeight: number,
  variantType: 'thumbnail' | 'medium' | 'full',
  variants?: DetailViewItem2Props['photo']['photoData']['variants']
): { valid: boolean; warning?: string; expectedDimensions?: string } {
  // Get properly typed variants array
  const variantsArray = getVariantsArray(variants as any);

  if (!variantsArray || variantsArray.length === 0) {
    return { valid: true };
  }

  // Find the variant metadata
  const variant = variantsArray.find(v => v.variantType === variantType);
  if (!variant) {
    return { valid: true }; // No metadata to validate against
  }

  // Check if dimensions match
  const dimensionsMatch =
    loadedWidth === variant.width &&
    loadedHeight === variant.height;

  if (!dimensionsMatch) {
    return {
      valid: false,
      warning: `⚠ Expected ${variantType}`,
      expectedDimensions: `${variant.width}x${variant.height}`,
    };
  }

  return { valid: true };
}

/**
 * Determine which variant type is currently being displayed based on the image URL
 * Also checks actual dimensions to detect URL mismatches
 *
 * Priority: Actual dimensions > URL matching
 * This prevents showing incorrect variant type when URLs are misconfigured
 */
export function getVariantTypeFromUrl(
  url: string,
  photo: DetailViewItem2Props['photo'],
  loadedDimensions?: { width: number; height: number } | null
): 'thumbnail' | 'medium' | 'full' | 'unknown' {
  // Get properly typed variants array
  const variants = getVariantsArray(photo.photoData.variants as any);

  // If we have loaded dimensions and variants, use actual dimensions (most reliable)
  if (loadedDimensions && variants && variants.length > 0) {
    const matchingVariant = variants.find(
      v => v.width === loadedDimensions.width && v.height === loadedDimensions.height
    );
    if (matchingVariant) {
      return matchingVariant.variantType as 'thumbnail' | 'medium' | 'full';
    }
  }

  // Fall back to URL matching if dimensions aren't available (without fallback logic)
  if (url === getActualVariantUrl(photo.photoData, 'thumbnail')) return 'thumbnail';
  if (url === getActualVariantUrl(photo.photoData, 'medium')) return 'medium';
  if (url === getActualVariantUrl(photo.photoData, 'full')) return 'full';

  return 'unknown';
}

/**
 * Convert PhotoItem2ViewModel to Photo format for useZoomPan hook
 * Simply returns the wrapped Photo object since PhotoItem2ViewModel now contains a Photo
 */
export function adaptPhotoData(photoData: DetailViewItem2Props['photo']): Photo {
  return photoData.photoData;
}
